'use client';

import { Room } from 'livekit-client';
import { useCallback } from 'react';
import { useBoolean } from 'usehooks-ts';

import { createContext } from '@/context/createContext';
import { isBrowser } from '@/utils/device-utils';

import { DeviceNotConnectedDialog } from '../rooms/[videoCode]/components/DeviceNotConnectedDialog';
import { DevicePermissionDeniedDialog } from '../rooms/[videoCode]/components/DevicePermissionDeniedDialog';
import { DevicePermissionPromptDialog } from '../rooms/[videoCode]/components/DevicePermissionPromptDialog';
import { DeviceSafariPermissionDeniedAndPromptedDialog } from '../rooms/[videoCode]/components/DeviceSafariPermissionDeniedAndPromptedDialog';
import { ShareScreenPermissionDeniedDialog } from '../rooms/[videoCode]/components/ShareScreenPermissionDeniedDialog';
import { ShareScreenWholeScreenDialog } from '../rooms/[videoCode]/components/ShareScreenWholeScreenDialog';

import type { ReactNode } from 'react';

type DevicesInfoContextState = {
  onDevicePermissionDenied: () => void;
  onDevicePermissionPrompt: (kind: MediaDeviceKind) => void;
  onDeviceNotConnected: (media: 'audio' | 'video') => void;
  onShareScreenPermissionDenied: () => void;
  onShareScreenWholeScreenRequired: () => void;
};

const [DevicesAlertDialogContextProviderComp, useDevicesAlertDialogContext] = createContext<DevicesInfoContextState>({
  name: 'DevicesAlertDialogContext',
});

const DevicesAlertContextProvider = ({ children }: { children: ReactNode }) => {
  const {
    value: isSafariDevicePermissionDeniedAndPromptOpen,
    setTrue: onSafariDevicePermissionDeniedAndPromptOpen,
    setFalse: onSafariDevicePermissionDeniedAndPromptClose,
  } = useBoolean();
  const {
    value: isDevicePermissionPromptOpen,
    // We would need to open this when we automatically try connecting to device but the permission state is prompt
    setTrue: onDevicePermissionPromptValueOpen,
    setFalse: onDevicePermissionPromptClose,
  } = useBoolean();
  const {
    value: isDevicePermissionDeniedOpen,
    setTrue: onDevicePermissionDeniedValueOpen,
    setFalse: onDevicePermissionDeniedClose,
  } = useBoolean();
  const {
    value: isDeviceNotConnectedModalOpen,
    setTrue: onDeviceNotConnectedModalOpen,
    setFalse: onDeviceNotConnectedModalClose,
  } = useBoolean();

  const {
    value: isShareScreenPermissionDeniedOpen,
    setTrue: onShareScreenPermissionDeniedOpen,
    setFalse: onShareScreenPermissionDeniedClose,
  } = useBoolean();

  const {
    value: isShareScreenWholeScreenOpen,
    setTrue: onShareScreenWholeScreenRequired,
    setFalse: onShareScreenWholeScreenClose,
  } = useBoolean();

  const onDevicePermissionPrompt = useCallback(
    async (kind: MediaDeviceKind) => {
      // Trigger prompt
      Room.getLocalDevices(kind, true).catch(e => {
        console.error(e);
      });
      if (isBrowser('safari')) {
        onSafariDevicePermissionDeniedAndPromptOpen();
        return;
      }

      onDevicePermissionPromptValueOpen();
    },
    [onSafariDevicePermissionDeniedAndPromptOpen, onDevicePermissionPromptValueOpen],
  );

  const onDevicePermissionDenied = useCallback(() => {
    if (isBrowser('safari')) {
      onSafariDevicePermissionDeniedAndPromptOpen();
      return;
    }
    onDevicePermissionDeniedValueOpen();
  }, [onSafariDevicePermissionDeniedAndPromptOpen, onDevicePermissionDeniedValueOpen]);

  const onDeviceNotConnected = useCallback(() => {
    onDeviceNotConnectedModalOpen();
  }, [onDeviceNotConnectedModalOpen]);

  const onShareScreenPermissionDenied = useCallback(() => {
    onShareScreenPermissionDeniedOpen();
  }, [onShareScreenPermissionDeniedOpen]);

  return (
    <DevicesAlertDialogContextProviderComp
      value={{
        onDeviceNotConnected,
        onDevicePermissionDenied,
        onDevicePermissionPrompt: (kind: MediaDeviceKind) => void onDevicePermissionPrompt(kind),
        onShareScreenPermissionDenied,
        onShareScreenWholeScreenRequired,
      }}
    >
      {children}
      {isBrowser('safari') ? (
        <DeviceSafariPermissionDeniedAndPromptedDialog
          open={isSafariDevicePermissionDeniedAndPromptOpen}
          onClose={onSafariDevicePermissionDeniedAndPromptClose}
        />
      ) : (
        <>
          <DevicePermissionPromptDialog open={isDevicePermissionPromptOpen} onClose={onDevicePermissionPromptClose} />
          <DevicePermissionDeniedDialog open={isDevicePermissionDeniedOpen} onClose={onDevicePermissionDeniedClose} />
        </>
      )}
      <DeviceNotConnectedDialog open={isDeviceNotConnectedModalOpen} onClose={onDeviceNotConnectedModalClose} />
      <ShareScreenPermissionDeniedDialog
        open={isShareScreenPermissionDeniedOpen}
        onClose={onShareScreenPermissionDeniedClose}
      />
      <ShareScreenWholeScreenDialog open={isShareScreenWholeScreenOpen} onClose={onShareScreenWholeScreenClose} />
    </DevicesAlertDialogContextProviderComp>
  );
};

export { DevicesAlertContextProvider, useDevicesAlertDialogContext };
